/**
 * Gate 2 wizard + latest-run restore.
 * Execution evidence lives in the API; step/selection/decision live in sessionStorage
 * so Gate 3 → Gate 2 navigation remounts the page without wiping the session.
 */

const storageKey = (projectId) => `validaite.gate2.session.${projectId}`;

export function readGate2Session(projectId) {
  if (!projectId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function writeGate2Session(projectId, state) {
  if (!projectId || !state) return;
  try {
    sessionStorage.setItem(storageKey(projectId), JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

export function retrievalMapFromRunResults(results = []) {
  const map = new Map();
  for (const row of results) {
    const id = row?.testCaseId;
    if (!id) continue;
    const chunks = Array.isArray(row.chunks) ? row.chunks : [];
    const answer = String(row.actualOutput || "").trim();
    let outcome = row.outcome;
    if (!["retrieved", "empty", "failed", "skipped"].includes(outcome)) {
      outcome = answer || chunks.length ? "retrieved" : "failed";
    }
    map.set(id, {
      prompt: row.prompt || "",
      outcome,
      chunks,
      answer,
      traces: row.traces || [],
      evidence: answer ? { actualOutput: answer, httpStatus: row.statusCode ?? null } : null,
      extras: {},
      statusCode: row.statusCode ?? null,
      latencyMs: row.latencyMs ?? null,
      askOk: Boolean(answer),
      askError: null,
      matchCount: row.matchCount ?? 0,
      usableCount: row.usableCount ?? 0,
      error: row.error || null,
    });
  }
  return map;
}

export function metricResultsFromScores(scores = []) {
  return (scores || []).map((s, i) => ({
    key: s.suiteId ? `${s.suiteId}:${s.metricId}` : s.metricId || String(i),
    metricId: s.metricId,
    metricName: s.metricName,
    suiteId: s.suiteId || null,
    suiteName: s.metricName,
    threshold: s.threshold,
    direction: s.direction,
    unit: s.unit,
    value: s.value,
    passed: s.passed,
    provider: s.provider || null,
    n: s.sampleCount,
    perCase: s.perCase || [],
    error: s.error || null,
  }));
}

export function selectionFromRun({ scores = [], results = [], metrics = [], testCases = [] } = {}) {
  const byMetric = new Set();
  const bySuite = new Set();
  for (const s of scores) {
    if (s.suiteId) bySuite.add(String(s.suiteId));
    const row = metrics.find((m) => m.metricId === s.metricId || m.formulaFn === s.metricId);
    if (row?.id != null) byMetric.add(String(row.id));
  }
  const casesById = new Map(testCases.map((tc) => [String(tc.id), tc]));
  for (const r of results) {
    const tc = casesById.get(String(r.testCaseId));
    if (tc?.metricId != null) byMetric.add(String(tc.metricId));
  }
  return { byMetric, bySuite };
}
